import groupBy from 'lodash.groupby';
import toPairs from 'lodash.topairs';
import React from 'react';
import Dropdown from 'react-dropdown';
import { Link } from 'react-router-dom';
import { formatDuration } from '../../helpers/duration-helper';
import { ItemList } from '../../components/item-list';
import { PlayButton } from '../../components/play-button';
import { ProgressBar } from '../../components/progress-bar';
import { ISubscriptionState } from '../../reducers/subscription-reducers';
import { IKidsModeState } from '../../reducers/kids-mode-reducers';
import { IItem } from '../../reducers/item-reducers';
import { CopyToClipboardButton } from '../../components/copy-to-clipboard-button';
import baseUrls from '../../data/baseUrls';

interface ItemDetailsEpisodesProps {
	item: IItem;
	isAuthenticated: boolean;
	subscription: ISubscriptionState;
	kidsMode: IKidsModeState;
	initialActiveSection?: string;
}

interface ItemDetailsEpisodesState {
	activeSection?: string;
}

export class ItemDetailsEpisodes extends React.Component<ItemDetailsEpisodesProps, ItemDetailsEpisodesState> {
	state: ItemDetailsEpisodesState = {};

	_getCoverOrientation(cover) {
		return cover && cover.height > cover.width ? 'portrait' : 'landscape';
	}

	_renderSubItem(item, cover, coverEl, isAuthenticated) {
		const startTime = item.progress && item.progress.kind === 'time' && item.progress.value ? item.progress.value : 0;
		const duration = item && item.duration ? formatDuration(item.duration) : null;

		const orientation = this._getCoverOrientation(cover);
		const imageWrapperStyle = { width: cover ? cover.width : undefined };

		const progress = item.duration > 0 ? (startTime / item.duration) * 100 : 0;
		const progressBar = progress ? (
			<div className={`${orientation}-progressBar`}>
				<ProgressBar progress={progress} />
			</div>
		) : (
			false
		);

		const canPlay =
			!(item.productInfo || item.productLink || item.promotion) ||
			(item.promotion === 'MOBILE-ED-TV-CHURCH' && isAuthenticated);

		return (
			<div key={item.id} className="subitem" itemScope itemType="http://schema.org/Episode">
				{item.promotion === 'MOBILE-ED-TV-CHURCH' && isAuthenticated ? (
					<div className={`${orientation}-image`} style={imageWrapperStyle}>
						<meta itemProp="thumbnailUrl" content={item.covers[0].url} />
						<Link to={'/media/' + item.id}>{coverEl}</Link>
						<PlayButton itemId={item.id} />
					</div>
				) : item.promotion ? (
					<div className={`${orientation}-image`} style={imageWrapperStyle}>
						<meta itemProp="thumbnailUrl" content={item.covers[0].url} />
						{coverEl}
					</div>
				) : item.productInfo || item.productLink ? (
					<div className={`${orientation}-image`} style={imageWrapperStyle}>
						<meta itemProp="thumbnailUrl" content={item.covers[0].url} />
						{coverEl}
					</div>
				) : (
					<div className={`${orientation}-image`} style={imageWrapperStyle}>
						<meta itemProp="thumbnailUrl" content={item.covers[0].url} />
						<Link to={'/media/' + item.id}>{coverEl}</Link>
						<PlayButton itemId={item.id} />
					</div>
				)}
				{canPlay ? (
					<Link to={'/media/' + item.id}>
						<h3 itemProp="name">{`${item.title}`}</h3>
					</Link>
				) : (
					<h3 itemProp="name">{`${item.title}`}</h3>
				)}
				{progressBar}
				{duration ? <div className="duration">{`${duration}`}</div> : false}
				<div className="description" itemProp="description">
					{item.description}
				</div>
			</div>
		);
	}

	_getCurrentSubItem(item: IItem) {
		if (item.subItems) {
			if (item.currentSubItem) {
				const subItem = item.subItems.find(x => x.id === item.currentSubItem);
				if (subItem) {
					return subItem;
				}
			}
			for (const subItem of item.subItems) {
				const progress = (subItem.progress && subItem.progress.value) || 0;
				const duration = subItem.duration || 0;
				if (progress / duration < 0.99) {
					return subItem;
				}
			}
		}
		return undefined;
	}

	render() {
		const { item, isAuthenticated, subscription, kidsMode } = this.props;
		if (!(item.subItems && item.subItems.length)) {
			return false;
		}

		const currentSubItem = this._getCurrentSubItem(item);

		const subItemGroups = toPairs(
			groupBy(item.subItems, x => (x.metadata && x.metadata.data ? x.metadata.data.sectionTitle : null) || '')
		);
		const sections = subItemGroups.map(x => x[0]);
		const activeSection =
			this.state.activeSection ||
			this.props.initialActiveSection ||
			(currentSubItem ? subItemGroups.find(group => group[1].includes(currentSubItem))[0] : sections[0]);

		const coverHeight = 170;
		const orientation = this._getCoverOrientation(item.subItems[0].covers[0]);

		const linkToSection = `${baseUrls.faithlifeTv}/items/${item.id}#${encodeURIComponent(activeSection)}`;

		return (
			<div className="item-details-episodes">
				{sections.length > 1 ? (
					<div className="sectionHeaderContainer">
						<Dropdown
							options={sections}
							value={activeSection}
							onChange={selection => this.setState({ activeSection: selection.value })}
						/>
						<CopyToClipboardButton textToCopy={linkToSection} className="copySection" copiedText="Section Link Copied">
							<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
								<path
									fill="#888"
									d="M14.02 14.02a2.1 2.1 0 0 0 0-2.97l-2.828-2.828 1.344-1.343 2.828 2.828a3.999 3.999 0 0 1 0 5.657 3.999 3.999 0 0 1-5.657 0L6.88 12.536l1.343-1.344 2.829 2.828a2.1 2.1 0 0 0 2.97 0zM8.294 2.636l2.828 2.828-1.343 1.344L6.949 3.98a2.1 2.1 0 1 0-2.97 2.97l2.829 2.828-1.343 1.343-2.829-2.828a3.999 3.999 0 0 1 0-5.657 3.999 3.999 0 0 1 5.657 0zm2.121 9.192L6.172 7.586l1.414-1.414 4.242 4.242-1.414 1.414z"
								/>
							</svg>
						</CopyToClipboardButton>
					</div>
				) : (
					false
				)}
				{subItemGroups.map(([section, items]) => {
					const isActive = section === activeSection;
					const currSubItemIndex =
						isActive && items.indexOf(currentSubItem) > -1 ? items.indexOf(currentSubItem) : undefined;
					const maxWidth = orientation === 'portrait' ? 440 : 300;
					return (
						<section key={section} className={isActive ? 'active' : ''}>
							{section ? <h3 className="sectionTitle">{section}</h3> : ''}
							<ItemList
								items={items}
								currSubItemIndex={currSubItemIndex}
								subItemWidth={300}
								maxWidth={maxWidth}
								imageHeight={coverHeight}
								renderItem={(item, cover, coverEl) => this._renderSubItem(item, cover, coverEl, isAuthenticated)}
								subscription={subscription}
								kidsMode={kidsMode}
								showFreeRibbon={false}
								lazy
							/>
						</section>
					);
				})}
			</div>
		);
	}
}
